import { FC, useContext } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'
import { Box, CircularProgress, Grid } from '@mui/material'

import { PageContext } from '@pages/FormCreateOrEdit'

import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { ScriptValueDialog } from '@components/ScriptValueDialog'

import { checkValidJS, getScriptValidationContext } from '@helpers'
import { MODAL_TYPE, ON_SAVE_FORM_COMMAND_TYPES } from '@constants'

import { useAddActionDialog } from './hooks'

export const AddActionDialog: FC = () => {
  const { t } = useTranslation()
  const { objectCode, showDialog, modalType, onCloseModal, isActionLoading } =
    useContext(PageContext)

  const {
    actionInputs,
    actionValueInputs,
    actionJSONValueInputs,
    methods,
    watchFormObject,
    isFormActionCode,
    watchJson,
    watchRedifinitionCommands,
    watchJsValue,
    isDirty,
    isLoading,
    showFormActionCommandsModal,
    isOpenScriptValueDialog,
    currentNameScriptDialog,
    setValue,
    handleShowFormActionCommandsModal,
    handleCloseFormActionCommandsModal,
    handleSave,
    setIsOpenScriptValueDialog,
  } = useAddActionDialog(objectCode)

  const handleSaveScriptValueDialog = (value: string) => {
    if (!currentNameScriptDialog) return

    setValue(currentNameScriptDialog, value, { shouldDirty: true })
  }

  const handleCloseScriptValueDialog = () => {
    setIsOpenScriptValueDialog(false)
  }

  return (
    <ModalWrapper
      disabledSave={!isDirty || isActionLoading}
      isShow={showDialog}
      title={`${t(`addDialog.${modalType}`)} Action`}
      containerStyle={{
        px: 0.5,
        py: 0.5,
        // borderRadius: 0
      }}
      onClose={() => onCloseModal(isDirty)}
      onSave={handleSave}
    >
      {isLoading && modalType === MODAL_TYPE.EDIT ? (
        <Grid container alignItems={'center'} justifyContent={'center'} my={3}>
          <CircularProgress />
        </Grid>
      ) : (
        <Box maxHeight={600} overflow='auto' pr={1} pt={1}>
          <FormProvider {...methods}>
            <form>
              <ModalWrapper
                disabledSave={!isDirty}
                isShow={showFormActionCommandsModal}
                title={`${t('addDialog.edit')} Object Form Commands`}
                containerStyle={{
                  width: 600,
                  maxWidth: 900,
                  maxHeight: 600,
                  overflowY: 'auto',
                  px: 0.5,
                  py: 0.5,
                  // borderRadius: 0,
                }}
                onClose={() => handleCloseFormActionCommandsModal()}
                onSave={() => handleCloseFormActionCommandsModal()}
              >
                <CommandPickerController
                  isAnotherObjectWithRequestData
                  commandName='name'
                  commandTypes={ON_SAVE_FORM_COMMAND_TYPES}
                  containerSx={{ mt: 1 }}
                  name='redefinitionCommands'
                  objectCode={objectCode}
                />
              </ModalWrapper>

              <FormGenerator inputs={actionInputs} mainGridSpacing={0} />
              {!watchJson && isFormActionCode && (
                <Grid display='flex'>
                  <FormGenerator isRow inputs={actionValueInputs} mainGridSpacing={0} />
                  <Button
                    disabled={!watchFormObject}
                    sx={{ ml: 1, width: 200 }}
                    variant='outlined'
                    onClick={handleShowFormActionCommandsModal}
                  >
                    {t('actionInputs.formCommands.label', {
                      mode: watchRedifinitionCommands?.length ? 'edit' : 'add',
                    })}
                  </Button>
                </Grid>
              )}
              <FormGenerator inputs={actionJSONValueInputs} mainGridSpacing={0} />
              <CommandPickerController
                isAnotherObjectWithRequestData
                isSingle
                isTypeHidden
                commandName='name'
                name='command'
                objectCode={objectCode}
              />
            </form>
            {isOpenScriptValueDialog && (
              <ScriptValueDialog
                windowBoundaries
                isShow={isOpenScriptValueDialog}
                modalType={'edit'}
                objectFields={[]}
                value={watchJsValue}
                validator={value =>
                  checkValidJS(
                    value,
                    getScriptValidationContext({
                      srcObj: { variables: {} },
                    })
                  )
                }
                onClose={handleCloseScriptValueDialog}
                onSave={handleSaveScriptValueDialog}
              />
            )}
          </FormProvider>
        </Box>
      )}
    </ModalWrapper>
  )
}
